import { z } from 'zod';

/**
 * Числа, зашитые в Telegram и в наши договорённости.
 *
 * Здесь только те константы, которые обязаны совпадать у api, worker и фронта.
 * Всё, что настраивается на инстанс (ключи, лимиты рассылки, TTL токенов),
 * живёт в .env и сюда не попадает.
 */

// ──────────────────────────── Лимиты Telegram ────────────────────────────

/** Максимум символов в text сообщения. Длиннее — резать на части. */
export const TG_MAX_MESSAGE_LENGTH = 4096;

/** Максимум символов в подписи к медиа. Втрое меньше сообщения — частая ловушка. */
export const TG_MAX_CAPTION_LENGTH = 1024;

/** Максимум символов в payload deep-link `t.me/<bot>?start=<code>`. */
export const TG_START_PAYLOAD_MAX = 64;

/**
 * Алфавит payload. Ограничение Telegram, а не наше: подписанный self-contained
 * токен (JWT) сюда не помещается ни по длине, ни по алфавиту — отсюда и берётся
 * схема с одноразовым кодом и обратным вызовом resolveUrl.
 */
export const TG_START_PAYLOAD_ALPHABET =
  'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789_-';
export const TG_START_PAYLOAD_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;

export const startPayloadSchema = z
  .string()
  .min(1)
  .max(TG_START_PAYLOAD_MAX)
  .regex(TG_START_PAYLOAD_PATTERN, 'start payload: допустимы только A-Za-z0-9_-');

/** Файлов в одном media group (альбоме). */
export const TG_MAX_MEDIA_GROUP = 10;

/** Потолок getFile: файл больше бота скачать нельзя, только переслать по file_id. */
export const TG_MAX_DOWNLOAD_BYTES = 20 * 1024 * 1024;

/** Потолок отправки файла ботом. */
export const TG_MAX_UPLOAD_BYTES = 50 * 1024 * 1024;

/**
 * Дефолты троттлинга (переопределяются TG_RATE_PER_CHAT_PER_SEC и
 * TG_RATE_PER_BOT_PER_SEC). Ровно на этих числах разваливались цепочки
 * шаблонов у предшественника: плотный цикл без пауз ⇒ 429 со второго блока.
 */
export const TG_RATE_PER_CHAT_PER_SEC = 1;
export const TG_RATE_PER_BOT_PER_SEC = 25;

/** Сколько ждать, если Telegram вернул 429 без внятного retry_after. */
export const TG_DEFAULT_RETRY_AFTER_SEC = 3;

/** Потолок паузы по retry_after: дольше — задача уходит в отложенный ретрай. */
export const TG_MAX_RETRY_AFTER_SEC = 300;

/** sendChatAction живёт 5 секунд, дальше индикатор гаснет. */
export const TG_CHAT_ACTION_TTL_SEC = 5;

// ──────────────────────────── Языки перевода ────────────────────────────

/**
 * Языки, между которыми разрешён перевод. ISO 639-1.
 * Список закрытый: код языка уходит в промпт переводчику и в ключ кэша, а
 * произвольная строка оттуда означает и мусор в кэше, и prompt injection.
 */
export const TRANSLATION_LANGS = [
  'ru',
  'en',
  'tr',
  'uk',
  'es',
  'pt',
  'de',
  'fr',
  'it',
  'pl',
  'ar',
  'fa',
  'hi',
  'az',
  'kk',
  'ky',
  'uz',
  'zh',
] as const;

export const translationLangSchema = z.enum(TRANSLATION_LANGS);
export type TranslationLang = z.infer<typeof translationLangSchema>;

/** Самоназвания для выпадающего списка: оператор ищет «Türkçe», а не «tr». */
export const TRANSLATION_LANG_LABELS: Record<TranslationLang, string> = {
  ru: 'Русский',
  en: 'English',
  tr: 'Türkçe',
  uk: 'Українська',
  es: 'Español',
  pt: 'Português',
  de: 'Deutsch',
  fr: 'Français',
  it: 'Italiano',
  pl: 'Polski',
  ar: 'العربية',
  fa: 'فارسی',
  hi: 'हिन्दी',
  az: 'Azərbaycanca',
  kk: 'Қазақша',
  ky: 'Кыргызча',
  uz: "O'zbekcha",
  zh: '中文',
};

export const DEFAULT_CONTACT_LANG: TranslationLang = 'tr';
export const DEFAULT_OPERATOR_LANG: TranslationLang = 'ru';

export function isTranslationLang(value: string): value is TranslationLang {
  return (TRANSLATION_LANGS as readonly string[]).includes(value);
}

// ──────────────────────────── Кэш контекста ────────────────────────────

/** Совпадает с Project.contextTtlSec по умолчанию в схеме БД. */
export const CONTEXT_CACHE_TTL_SEC = 60;

/**
 * Сколько ещё держим протухшую карточку после истечения TTL.
 * Проект недоступен — показываем устаревшие данные с пометкой, а не пустую
 * панель: поддержка не должна падать вместе с продуктом.
 */
export const CONTEXT_CACHE_STALE_TTL_SEC = 15 * 60;

/** Ключ карточки в Redis. Контакт, а не telegram_id: у проекта может быть несколько ботов. */
export function contextCacheKey(projectId: string, contactId: string): string {
  return `ctx:${projectId}:${contactId}`;
}

// ──────────────────────────── Вызовы коннектора ────────────────────────────

/** Таймаут HTTP к проекту. Оператор ждёт карточку, а не чужой медленный ответ. */
export const CONNECTOR_TIMEOUT_MS = 5_000;

/** Действия выполняются в проекте и могут быть медленнее чтения. */
export const CONNECTOR_ACTION_TIMEOUT_MS = 15_000;

/** Потолок тела запроса ingest: защита от заливки мусора по валидному ключу. */
export const CONNECTOR_MAX_BODY_BYTES = 256 * 1024;

// ──────────────────────────── Диалоги ────────────────────────────

/**
 * Сколько часов после закрытия тикета продолжаем писать в него, а не заводим
 * новый (переопределяется CONVERSATION_REOPEN_WINDOW_HOURS).
 */
export const CONVERSATION_REOPEN_WINDOW_HOURS = 24;

/** Порог SLA-строки над списком: сколько клиентов ждут дольше этого. */
export const SLA_WARNING_MINUTES = 10;

/** Превью последнего сообщения в списке диалогов. */
export const CONVERSATION_PREVIEW_LENGTH = 120;
